import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import { useAppDispatch } from 'src/hooks/useAppDispatch';
import { useAppSelector } from 'src/hooks/useAppSelector';
import useAuth from 'src/hooks/useAuth';
import { useApiRequest } from 'src/hooks/useApi';
import { ClassifierCategories } from 'src/types/dataTypes';
import useStyles from 'src/styles/di-theme';
import {
	fetchStatusData,
	fetchPrevious24HoursData,
	exportESFeeds,
	fetchAccountESFeeds,
} from 'src/features/detections/thunks';
import { DateTime } from 'luxon';
import {
	COLUMNS_WITHOUT_SORTING,
	SORT_FIELD_MAP,
	SORT_FIELD_REVERSE_MAP,
	TAB_COLUMNS,
	detectionSearchFields,
	SIDE_MENU_DROPDOWNS_TAB_01,
	SIDE_MENU_DROPDOWNS_TAB_2,
	COLUMN_VISIBILITY_INITIAL_STATE_TAB_01,
	COLUMN_VISIBILITY_INITIAL_STATE_TAB_2,
} from './constants';
import { buildDisplayString } from './helper';
import { type CategoryGroup } from 'src/components/Filters/DIConditionCheckboxFilter';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { renderCell } from './cellRenderers';
import {
	resetDetectionState,
	updateDateRange,
	updatePage,
	updatePageSize,
	updateSearchTextAndField,
	updateSort,
	updateFilters,
} from 'src/features/detections/detectionSlice';
import type { DetectionFilter } from 'src/features/detections/detectionSlice';
import { debounce } from '@material-ui/core';
import { batch } from 'react-redux';
import { parseSearchValue } from '../scanlog/helper';
import { useDetectionStyles } from './detectionsStyles';
import useToast from 'src/hooks/useToast';

// Accessibility props function
function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
	};
}

export const useDetectionsDataTableLogic = () => {
	// =======================
	// STATE & HOOKS
	// =======================

	const { auth } = useAuth();
	const { api } = useAppToolchain();
	const dispatch = useAppDispatch();
	const customClasses = useDetectionStyles();
	const classes = useStyles();
	const [tab, setTab] = React.useState(0);

	// Redux state
	const { statusData, loading, error } = useAppSelector((store) => ({
		statusData: store.detectionStatus.statusData,
		loading: store.detectionStatus.loading,
		error: store.detectionStatus.error,
	}));
	const detectionState = useAppSelector((store) => store.detections);

	// Tab configuration
	const tabsArray = [
		{ label: 'Unresolved', ...a11yProps(0) },
		{ label: 'In Progress', ...a11yProps(1) },
		{ label: 'Resolved', ...a11yProps(2) },
	];

	// Local state — UI panels
	const [expandPanel, setExpandPanel] = React.useState(false);
	const [selectedFeed, setSelectedFeed] = React.useState(null);
	const [showShimmer, setShowShimmer] = useState<boolean>(true);
	const [rowSelection, setRowSelection] = useState<Record<string, boolean>>(
		{},
	);
	const [showDismiss, setShowDismiss] = useState(false);
	const [remediateOption, setRemediateOption] = useState('Dismiss');
	const { toasts, triggerToast } = useToast();

	// Local state — side menu
	const [sideMenuSearchValue, setSideMenuSearchValue] = useState<string>('');

	// Column visibility — each tab has its own independent localStorage state
	const [colVis0, setColVis0] = useLocalStorage(
		'detectionsColumnVisibility_0',
		COLUMN_VISIBILITY_INITIAL_STATE_TAB_01,
	);
	const [colVis1, setColVis1] = useLocalStorage(
		'detectionsColumnVisibility_1',
		COLUMN_VISIBILITY_INITIAL_STATE_TAB_01,
	);
	const [colVis2, setColVis2] = useLocalStorage(
		'detectionsColumnVisibility_2',
		COLUMN_VISIBILITY_INITIAL_STATE_TAB_2,
	);

	// Sorting state
	const [sorting, setSorting] = useState<
		Array<{ id: string; desc: boolean }>
	>(() =>
		detectionState.sort
			? [
					{
						id:
							SORT_FIELD_REVERSE_MAP[detectionState.sort] ||
							detectionState.sort,
						desc: detectionState.sortDir === 'desc',
					},
			  ]
			: [{ id: 'lastModified', desc: true }],
	);

	// Local state — date picker
	const [datePickerError, setDatePickerError] = useState<boolean>(false);
	const [datePickerErrorMessage, setDatePickerErrorMessage] =
		useState<string>('');

	// Local state — search
	const [searchValue, setSearchValue] = useState<string>('');
	const searchBarRef = React.useRef<HTMLElement>(null);
	const onSearchBarClickRef = React.useRef<(() => void) | null>(null);

	// Local state — column visibility drawer
	const [isColumnVisibilityDrawerOpen, setIsColumnVisibilityDrawerOpen] =
		useState<boolean>(false);

	// Classifier categories API request
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [categoryState, categoryResponse, categoryErr] =
		useApiRequest<ClassifierCategories>(() => {
			return api.getClassifierCategories(auth.accountDetails.account.id);
		}, [auth.accountDetails.account.id]);

	// =======================
	// MEMOIZED VALUES
	// =======================

	const columnVisibility = useMemo(
		() => (tab === 2 ? colVis2 : tab === 1 ? colVis1 : colVis0),
		[tab, colVis0, colVis1, colVis2],
	);

	const sideMenuDropdowns = useMemo(() => {
		const dropdowns =
			tab === 2 ? SIDE_MENU_DROPDOWNS_TAB_2 : SIDE_MENU_DROPDOWNS_TAB_01;
		return dropdowns.map((dropdown) => ({
			...dropdown,
			items: dropdown.items.filter((item) =>
				item.label
					.toLowerCase()
					.includes(sideMenuSearchValue.toLowerCase()),
			),
		}));
	}, [tab, sideMenuSearchValue]);

	// Build classifier category groups for filter rendering
	const classifierCategories = useMemo((): CategoryGroup[] => {
		if (!categoryResponse) return [];
		const classifierMap: Record<string, string> = {
			ContentClassifiers: 'Content Classifiers',
			SharingClassifiers: 'Sharing Classifiers',
			CustomClassifiers: 'Custom Classifiers',
		};
		const groups: CategoryGroup[] = [];
		for (const key in categoryResponse) {
			const categoryOptions = new Set<string>(categoryResponse[key]);
			if (classifierMap[key] === 'Content Classifiers') {
				categoryOptions.add('Malicious');
			}
			groups.push({
				name: classifierMap[key],
				options: [...categoryOptions]
					.sort()
					.map((val) => ({ label: val, value: val })),
			});
		}
		return groups;
	}, [categoryResponse]);

	// Debounced dispatch for search field
	const debouncedDispatch = useMemo(
		() =>
			debounce((field, text) => {
				dispatch(updateSearchTextAndField({ text, field }));
				dispatch(updatePage(1));
			}, 500),
		[dispatch],
	);

	// Table columns definition based on active tab
	const columns = useMemo(
		() =>
			(TAB_COLUMNS[tab] || TAB_COLUMNS[0]).map(
				({ accessorKey, header }) => ({
					accessorKey,
					header,
					enableColumnActions: false,
					Cell: ({ cell, row }) =>
						renderCell(accessorKey, cell, row, tab),
					enableSorting:
						COLUMNS_WITHOUT_SORTING.indexOf(accessorKey) === -1,
					sortDescFirst: true,
					enableColumnFilter: false,
				}),
			),
		[tab],
	);

	// Date range as Luxon DateTime objects
	const dateRange = useMemo(() => {
		const start = detectionState.dateRangeStart
			? DateTime.fromISO(detectionState.dateRangeStart).toLocal()
			: null;
		const end = detectionState.dateRangeEnd
			? DateTime.fromISO(detectionState.dateRangeEnd).toLocal()
			: null;
		return [start, end] as [DateTime | null, DateTime | null];
	}, [detectionState.dateRangeStart, detectionState.dateRangeEnd]);

	// Derive selected feed IDs from rowSelection state
	const selectedFeedIds = useMemo(
		() => Object.keys(rowSelection).filter((key) => rowSelection[key]),
		[rowSelection],
	);

	// =======================
	// CALLBACKS
	// =======================

	// Tab change handler
	const handleChange = useCallback(
		(event: React.SyntheticEvent, newValue: number | string) => {
			batch(() => {
				dispatch(updateFilters([]));
				dispatch(updatePage(1));
			});
			setSearchValue(
				buildDisplayString(
					detectionState.searchField,
					detectionState.searchText,
					[],
				),
			);
			setTab(
				typeof newValue === 'string' ? parseFloat(newValue) : newValue,
			);
		},
		[dispatch, detectionState.searchField, detectionState.searchText],
	);

	// Tab switch wrapper for StatusComponentV2 (runs same cleanup as handleChange)
	const switchTab = useCallback(
		(newTab: number) => {
			handleChange(null, newTab);
		},
		[handleChange],
	);

	// Date range change handler
	const handleDateRangeChange = useCallback(
		(dateRange: [DateTime | null, DateTime | null]) => {
			const [start, end] = dateRange;
			const currentDay = DateTime.local().endOf('day');
			const last90Days = DateTime.local()
				.minus({ days: 90 })
				.startOf('day');

			if (
				!start?.isValid ||
				!end?.isValid ||
				start > end ||
				start > currentDay ||
				start < last90Days ||
				end > currentDay ||
				end < last90Days
			) {
				setDatePickerError(true);
				setDatePickerErrorMessage('Please select valid date');
				return;
			}

			setDatePickerError(false);
			setDatePickerErrorMessage('');

			// If end date is today, use current time; otherwise use endOf('day') for full day coverage
			const now = DateTime.local();
			const endValue = end.hasSame(now, 'day') ? now : end.endOf('day');

			dispatch(
				updateDateRange({
					start: start.startOf('day').toISO(),
					end: endValue.toISO(),
				}),
			);
			dispatch(updatePage(1));
		},
		[dispatch],
	);

	// Search field change handler
	const handleSearchFieldChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const val = e.target.value;
			const { field, text } = parseSearchValue(
				val,
				detectionSearchFields,
			);

			setSearchValue(val);

			if (
				field !== detectionState.searchField ||
				text !== detectionState.searchText
			) {
				debouncedDispatch(field, text);
			}
		},
		[
			detectionState.searchField,
			detectionState.searchText,
			debouncedDispatch,
		],
	);

	// Clear search handler
	const handleClearSearch = useCallback(() => {
		setSearchValue('');
		batch(() => {
			dispatch(updateSearchTextAndField({ text: '', field: '' }));
			dispatch(updateFilters([]));
			dispatch(updatePage(1));
		});
	}, [dispatch]);

	// Apply filters from popover handler
	const onApplyFilters = useCallback(
		(
			searchField: string,
			searchText: string,
			newFilters: DetectionFilter[],
		) => {
			setSearchValue(
				buildDisplayString(searchField, searchText, newFilters),
			);
			batch(() => {
				dispatch(
					updateSearchTextAndField({
						text: searchText,
						field: searchField,
					}),
				);
				dispatch(updateFilters(newFilters));
				dispatch(updatePage(1));
			});
		},
		[dispatch],
	);

	// Pagination change handler
	const handlePaginationChange = useCallback(
		(event, value) => {
			dispatch(updatePage(value));
		},
		[dispatch],
	);

	// Items per page change handler
	const handleOnClickItemsPerPageOption = useCallback(
		(itemsPerPageValue: number) => {
			dispatch(updatePageSize(itemsPerPageValue));
		},
		[dispatch],
	);

	// Row click handler (tab 0 only)
	const handleRowClick = useCallback(
		(feed) => {
			if (tab === 0) {
				setSelectedFeed(feed);
				setShowShimmer(true);
			}
		},
		[tab],
	);

	// Remediate option selection handler
	const handleRemediate = useCallback((option: string) => {
		setRemediateOption(option);
		setShowDismiss(true);
	}, []);

	// Refresh end date to current time (used by SideDetailPanel)
	const refreshEndDate = useCallback(() => {
		const endDate = DateTime.fromISO(detectionState.dateRangeEnd).toLocal();
		const newEnd = endDate.hasSame(DateTime.local(), 'day')
			? DateTime.local().toUTC().toISO()
			: endDate.endOf('day').toUTC().toISO();
		dispatch(
			updateDateRange({
				start: detectionState.dateRangeStart,
				end: newEnd,
			}),
		);
	}, [dispatch, detectionState.dateRangeStart, detectionState.dateRangeEnd]);

	// Remediate popup close handler
	const handleRemediateClose = useCallback(
		(refresh: boolean) => {
			if (refresh) {
				setRowSelection({});
				refreshEndDate();
				triggerToast({
					type: 'success',
					message:
						"Marked records are under remediation, viewable and updated in real-time in the 'In Progress' tab.",
					duration: 6000,
				});
			}
			setShowDismiss(false);
		},
		[refreshEndDate, triggerToast],
	);

	// Download CSV callback
	const downloadFile = useCallback(() => {
		const status =
			tab === 0
				? 'Open'
				: tab === 1
				? 'RemediationInitiated'
				: 'Dismissed';
		dispatch(
			exportESFeeds({
				api,
				id: auth.accountDetails.account.id,
				startDate: detectionState.dateRangeStart,
				endDate: detectionState.dateRangeEnd,
				searchText: detectionState.searchText,
				searchField: detectionState.searchField,
				filters: detectionState.filters,
				sort: detectionState.sort,
				sortDir: detectionState.sortDir,
				status,
			}),
		)
			.unwrap()
			.catch(() => {
				triggerToast({
					type: 'error',
					message: 'Error exporting CSV. Please try again later.',
					duration: 6000,
				});
			});
	}, [
		tab,
		dispatch,
		api,
		auth.accountDetails.account.id,
		detectionState.dateRangeStart,
		detectionState.dateRangeEnd,
		detectionState.searchText,
		detectionState.searchField,
		detectionState.filters,
		detectionState.sort,
		detectionState.sortDir,
		triggerToast,
	]);

	// Refresh callback
	const handleRefresh = useCallback(() => {
		const endDate = detectionState.dateRangeEnd
			? DateTime.fromISO(detectionState.dateRangeEnd).toLocal()
			: null;
		let newEnd = endDate;
		if (endDate && endDate.hasSame(DateTime.local(), 'day')) {
			newEnd = DateTime.local();
		} else if (endDate) {
			newEnd = endDate.endOf('day');
		}
		dispatch(
			updateDateRange({
				start: detectionState.dateRangeStart,
				end: newEnd?.toUTC().toISO(),
			}),
		);
	}, [dispatch, detectionState.dateRangeStart, detectionState.dateRangeEnd]);

	// Search callback from SideDetailPanel
	const handlePanelSearch = useCallback(
		(search: string) => {
			const { field, text } = parseSearchValue(
				search,
				detectionSearchFields,
			);
			setExpandPanel(false);
			setSelectedFeed(null);
			setSearchValue(
				buildDisplayString(field, text, detectionState.filters),
			);
			batch(() => {
				dispatch(updateSearchTextAndField({ text, field }));
				dispatch(updatePage(1));
			});
		},
		[dispatch, detectionState.filters],
	);

	// Side menu checkbox change handler
	const handleSideMenuCheckboxChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { value, checked } = event.target;
			if (tab === 2) {
				setColVis2({ ...colVis2, [value]: checked });
			} else if (tab === 1) {
				setColVis1({ ...colVis1, [value]: checked });
			} else {
				setColVis0({ ...colVis0, [value]: checked });
			}
		},
		[tab, colVis0, setColVis0, colVis1, setColVis1, colVis2, setColVis2],
	);

	// =======================
	// EFFECTS
	// =======================

	// Status data fetch + polling (5 min interval)
	useEffect(() => {
		if (!auth.isFullAdmin()) return;

		let isFetching = false;

		const performFetch = async () => {
			if (isFetching) return;
			isFetching = true;

			try {
				await dispatch(
					fetchStatusData({
						api,
						accountId: auth.accountDetails.account.id,
					}),
				).unwrap();

				await dispatch(
					fetchPrevious24HoursData({
						api,
						accountId: auth.accountDetails.account.id,
					}),
				).unwrap();
			} catch (error) {
				console.error('Error on fetching status data', error);
			} finally {
				isFetching = false;
			}
		};

		performFetch();

		const intervalId = setInterval(() => {
			performFetch();
		}, 300000);

		return () => {
			clearInterval(intervalId);
		};
	}, [auth.isFullAdmin(), dispatch, api, auth.accountDetails.account.id]);

	// ES feeds fetch (with abort on dependency change)
	useEffect(() => {
		const status =
			tab === 0
				? 'Open'
				: tab === 1
				? 'RemediationInitiated'
				: 'Dismissed';

		const promise = dispatch(
			fetchAccountESFeeds({
				api,
				accountId: auth.accountDetails.account.id,
				startDate: detectionState.dateRangeStart,
				endDate: detectionState.dateRangeEnd,
				searchText: detectionState.searchText,
				searchField: detectionState.searchField,
				filters: detectionState.filters,
				page: detectionState.page,
				pageSize: detectionState.pageSize,
				sortBy: detectionState.sort,
				sortOrder: detectionState.sortDir,
				status,
			}),
		);

		return () => {
			promise.abort();
		};
	}, [
		tab,
		detectionState.page,
		detectionState.pageSize,
		detectionState.dateRangeStart,
		detectionState.dateRangeEnd,
		detectionState.searchText,
		detectionState.searchField,
		detectionState.filters,
		detectionState.sort,
		detectionState.sortDir,
		dispatch,
		api,
		auth.accountDetails.account.id,
	]);

	// Reset detection state on unmount to clear stale state for next visit
	useEffect(() => {
		return () => {
			dispatch(resetDetectionState());
		};
	}, [dispatch]);

	// Clear row selection when tab changes
	useEffect(() => {
		setRowSelection({});
	}, [tab]);

	// Sorting sync with Redux
	useEffect(() => {
		if (sorting.length > 0) {
			const { id, desc } = sorting[0];
			const backendField = SORT_FIELD_MAP[id] || id;
			const dir = desc ? 'desc' : 'asc';
			if (
				backendField !== detectionState.sort ||
				dir !== detectionState.sortDir
			) {
				dispatch(updateSort({ sort: backendField, sortDir: dir }));
			}
		} else if (
			detectionState.sort !== 'lastUpdated' ||
			detectionState.sortDir !== ''
		) {
			dispatch(updateSort({ sort: 'lastUpdated', sortDir: '' }));
		}
	}, [sorting, dispatch, detectionState.sort, detectionState.sortDir]);

	// =======================
	// RETURN
	// =======================

	return {
		// State
		auth,
		tab,
		statusData,
		loading,
		detectionState,
		expandPanel,
		setExpandPanel,
		selectedFeed,
		setSelectedFeed,
		showShimmer,
		setShowShimmer,
		rowSelection,
		setRowSelection,
		showDismiss,
		remediateOption,
		toasts,
		searchValue,
		searchBarRef,
		onSearchBarClickRef,
		isColumnVisibilityDrawerOpen,
		setIsColumnVisibilityDrawerOpen,
		sideMenuSearchValue,
		setSideMenuSearchValue,
		datePickerError,
		datePickerErrorMessage,
		sorting,
		columnVisibility,
		categoryResponse,

		// Memoized
		tabsArray,
		columns,
		dateRange,
		selectedFeedIds,
		classifierCategories,
		sideMenuDropdowns,
		customClasses,
		classes,

		// Handlers
		handleChange,
		handleDateRangeChange,
		handleSearchFieldChange,
		handleClearSearch,
		onApplyFilters,
		handlePaginationChange,
		handleOnClickItemsPerPageOption,
		handleRowClick,
		handleRemediate,
		handleRemediateClose,
		downloadFile,
		handleRefresh,
		handlePanelSearch,
		handleSideMenuCheckboxChange,
		refreshEndDate,
		switchTab,
		setSorting,
	};
};
